import { unexpectedLiteErrorMessage } from "@/constants/validators/messages"

interface ErrorBody {
  code?: string
  name?: string
  detail?: string
  message?: string
  id?: string
}

type NetworkErrorStruct = {
  originalError?: unknown
  url?: string
  name: string
  detail: string
  code: string
  id: string
  status: number
}
const genericErrorMessage: NetworkErrorStruct = {
  name: "Unexpected error",
  detail: unexpectedLiteErrorMessage,
  url: "",
  code: "unexpected_error",
  id: "",
  status: 500
}

export class NetworkError extends Error {
  // What we expect from the API response. We'll handle this in the parseError function, if the error is an instance of NetworkError.
  body: NetworkErrorStruct

  constructor(body: unknown, res: Response | XMLHttpRequest) {
    super()

    try {
      Error.captureStackTrace(this, NetworkError)
    } catch {
      // noop
    }

    // Type guard to safely access properties on body
    const errorBody =
      typeof body === "object" && body !== null
        ? (body as ErrorBody)
        : ({
            code: genericErrorMessage.code,
            name: genericErrorMessage.name,
            detail: genericErrorMessage.detail,
            id: genericErrorMessage.id,
            message: genericErrorMessage.detail
          } satisfies ErrorBody)

    // If the body isn't adhering to the expected structure, we'll use a generic error message.
    this.body = {
      originalError: body,
      url: "url" in res ? res.url : res.responseURL,
      code: errorBody.code || genericErrorMessage.code,
      name: errorBody.name || (res.status ? `HTTP ${res.status}` : genericErrorMessage.name),
      detail: errorBody.detail || errorBody.message || res.statusText || genericErrorMessage.detail,
      id: errorBody.id || genericErrorMessage.id,
      status: res.status || genericErrorMessage.status
    } satisfies NetworkErrorStruct
  }
}
/**
 * API errors reach us in two shapes: a `NetworkError` (thrown by
 * `unwrapResponse`, status on `body`) and the react-query clients' thrown
 * response body (status on `response`).
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof NetworkError) return error.body.status

  if (typeof error === "object" && error !== null && "response" in error) {
    return (error as { response?: { status?: number } }).response?.status
  }

  return undefined
}

/**
 * 4xx statuses that are transient rather than an answer about the resource.
 * 401 is in here because the access token is fetched per request, so a second
 * attempt carries a fresh one and can genuinely succeed.
 */
const TRANSIENT_CLIENT_ERRORS = new Set([401, 408, 425, 429])

/**
 * Whether the server has given a determinate answer about this resource that
 * retrying will not change — gone (404/410), not ours (403), or a request we
 * cannot make (400/422). Expired credentials, rate limiting and timeouts are
 * excluded: they say nothing about the resource.
 */
export function isDeterminateClientError(error: unknown) {
  const status = getErrorStatus(error)

  return status !== undefined && status >= 400 && status < 500 && !TRANSIENT_CLIENT_ERRORS.has(status)
}

/**
 * Tries unwrapping the response from the API, parsing the content type and returning a typed response.
 * If the
 * @param res
 * @returns
 */

export async function unwrapResponse<T = unknown>(res: Response | XMLHttpRequest) {
  try {
    // TODO: Improve XMLHttpRequest support
    if (res instanceof XMLHttpRequest) {
      if (res.status >= 400) {
        const body = JSON.parse(res.responseText)
        throw new NetworkError(body, res)
      }

      // ! What if this is not a JSON response?
      return JSON.parse(res.responseText) as T
    }

    if (!res.ok) {
      const body = await res.json()
      throw new NetworkError(body, res)
    }

    const content_type = res.headers.get("content-type")
    const blobs = [
      // ZIP files
      "application/zip",
      // Binary content
      "application/octet-stream",
      // Generic binary content type for images
      "application/image",
      // Will accept any image/* content type
      "image/"
    ]

    if (["json", "type=collection"].some(key => content_type?.includes(key))) {
      return res.json() as T
    }

    if (blobs.some(key => content_type?.includes(key))) {
      return res.blob() as T
    }

    if (content_type?.includes("text/plain")) {
      return res.text() as T
    }

    // Unknown content type, we weren't able to parse the response content type: hopefully this is a JSON response.
    if (content_type) {
      return res.json() as T
    }

    return {} as T
  } catch (e) {
    // If the error is already a NetworkError, we'll just pass it along.
    if (e instanceof NetworkError) throw e

    // ... unless something went really sideways, in which case we'll use a generic error message.
    throw new NetworkError(
      {
        detail: unexpectedLiteErrorMessage,
        code: "unexpected_error",
        id: "unexpected_error",
        status: 500
      },
      res
    )
  }
}

/**
 * Parses different types of errors, extracting specific
 * properties for NetworkError and providing a generic error message for other types of errors.
 *
 * We're hoping errors are uniform, but we're not sure. In case they're not, we'll use a generic error message.
 */
export function parseError<T>(error: T) {
  let networkError: NetworkError | undefined
  if (error instanceof NetworkError) {
    networkError = error
  } else if (typeof error === "object" && error !== null && "response" in error && error.response instanceof Response) {
    networkError = new NetworkError(error, error.response)
  }

  if (networkError) {
    const { body } = networkError
    const { originalError, ...rest } = body

    return {
      original: originalError as T,
      body: rest
    }
  }

  // We're not handling a NetworkError, so we'll return a generic error message.
  return {
    original: error,
    body: genericErrorMessage
  }
}
